import { ApiError } from "@carepass/api-client";
import { useEffect } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { parseAdminEntityId, parseAdminListSearch, pathForReconciliationIssue } from "../../app/routes";
import { StateNotice } from "../workspace/shared";
import { ReconciliationRunDetailView } from "./components/ReconciliationRunDetailView";
import { useReconciliationRun, useReconciliationRunIssues } from "./queries";
import { parseIssueStatus } from "./schema";

function detailState(query: { isPending: boolean; isError: boolean; error: unknown }) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function ReconciliationRunDetailPageForRun({ runId }: { readonly runId: number }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const issueStatus = parseIssueStatus(searchParams.get("issueStatus"));
  const list = parseAdminListSearch(searchParams, { page: 0, size: 20 });
  const run = useReconciliationRun(runId);
  const issues = useReconciliationRunIssues(runId, { status: issueStatus, page: list.page, size: list.size });
  const navigation = useAdminNavigation({
    contextKey: `reconciliation-run:${runId}`,
    defaultListPath: "/operations/reconciliations?tab=runs",
    originLabel: "대사 실행 상세",
  });

  const paramsFor = (status: typeof issueStatus, page: number) => {
    const params = new URLSearchParams({ page: String(page), size: String(list.size) });
    if (status) params.set("issueStatus", status);
    return params;
  };

  useEffect(() => {
    const canonical = paramsFor(issueStatus, list.page);
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true, state: location.state });
  }, [issueStatus, list.page, list.size, location.state, searchParams, setSearchParams]);

  useEffect(() => {
    if (!issues.data || issues.data.items.length > 0 || issues.data.totalCount === 0 || list.page === 0) return;
    const lastPage = Math.max(0, Math.ceil(issues.data.totalCount / list.size) - 1);
    setSearchParams(paramsFor(issueStatus, lastPage), { replace: true, state: location.state });
  }, [issueStatus, issues.data, list.page, list.size, location.state, setSearchParams]);

  return <ReconciliationRunDetailView
    runId={runId}
    run={run.data}
    runState={detailState(run)}
    issues={issues.data}
    issuesState={issues.isPending ? "loading" : issues.isError ? "error" : "ready"}
    issueStatus={issueStatus}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    issueHref={(issueId) => pathForReconciliationIssue(runId, issueId)}
    onOpenIssue={(issue) => navigation.openRelated(pathForReconciliationIssue(runId, issue.id), "대사 실행 상세")}
    onIssueStatusChange={(status) => setSearchParams(paramsFor(status, 0), { state: location.state })}
    onPageChange={(page) => setSearchParams(paramsFor(issueStatus, page), { state: location.state })}
    onRetryRun={() => { void run.refetch(); }}
    onRetryIssues={() => { void issues.refetch(); }}
  />;
}

export function ReconciliationRunDetailPage() {
  const runId = parseAdminEntityId(useParams().runId);
  if (runId === null) return <StateNotice state="not-found" />;
  return <ReconciliationRunDetailPageForRun key={runId} runId={runId} />;
}
